import { validatePhone } from './phoneUtil';
import {
  CARD_ID,
  EXPIRATION_DATE_ID,
  POSTAL_CODE_ID,
  SECURITY_CODE_ID,
} from '../components/CreditCardDetails/constants';
import {
  AUSTRALIAN_COUNTRY_CODE,
  CANADIAN_COUNTRY_CODE,
  US_COUNTRY_CODE,
  US_STATES,
  CANADIAN_PROVINCES,
} from '../components/BusinessDetails';
import objectIsEmpty from './objectIsEmpty';

export const hasInvalidState = (state) => {
  return !US_STATES.includes(state) && !CANADIAN_PROVINCES.includes(state);
};

export const validateEmail = (email) => {
  return /^[a-z0-9!#$%&'*+/=?^_‘{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_‘{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/.test(
    email?.toLowerCase()
  );
};

// taken from niffler validators
export const isPostalCode = (value, country = 'US') => {
  let re;

  switch (country) {
    case US_COUNTRY_CODE:
      re = new RegExp(/^(?!0{5})(\d{5})(?!-?0{4})(|-\d{4})?$/);
      break;
    case CANADIAN_COUNTRY_CODE:
      // Alpha-numeric with optional space or dash in the middle
      re = new RegExp(
        /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ -]?\d[ABCEGHJ-NPRSTV-Z]\d$/i
      );
      break;
    case AUSTRALIAN_COUNTRY_CODE:
      // 0000 OR 000 First two digits 02, 08 or 20-97. Leading 0 may be omitted. 909 and 0909 are valid as well - but no other postal codes starting with 9 or 09.
      re = new RegExp(/^(?:(?:[2-8]\d|9[0-7]|0?[28]|0?9(?=09))(?:\d{2}))$/);
      break;
    default:
      re = new RegExp(/^(?!0{5})(\d{5})(?!-?0{4})(|-\d{4})?$/);
  }

  return re.test(value);
};

export const hasInvalidPassword = (password) =>
  !password || password.length < 8;

const buildErrorMessage = (error) => `Please enter a ${error}.`;

export const getEmptyErrorMessage = (
  fieldName,
  zipCodeText = 'ZIP / Postal'
) => {
  switch (fieldName) {
    case 'city':
      return buildErrorMessage('valid city');
    case 'password':
      return buildErrorMessage(fieldName);
    case 'address':
      return buildErrorMessage('valid address');
    case 'country':
      return `Please select a country.`;
    case 'email':
      return buildErrorMessage('valid email');
    case 'firstName':
      return buildErrorMessage('first name');
    case 'lastName':
      return buildErrorMessage('last name');
    case 'name':
      return buildErrorMessage('business name');
    case 'phone':
      return buildErrorMessage('valid 10-digit mobile number');
    case 'state':
      return 'Please select a State/Province.';
    case CARD_ID:
      return buildErrorMessage('valid card number');
    case EXPIRATION_DATE_ID:
      return 'Please enter an expiration date';
    case POSTAL_CODE_ID:
      return buildErrorMessage(`billing ${zipCodeText} code`);
    case SECURITY_CODE_ID:
      return buildErrorMessage('security code');
    case 'zip':
      return buildErrorMessage(`valid ${zipCodeText} code`);
    default:
      return null;
  }
};

export const getValidationErrorMessage = (
  fieldName,
  value,
  zipCodeText = 'ZIP / Postal'
) => {
  switch (fieldName) {
    case 'email':
      return !validateEmail(value) && buildErrorMessage('valid email');
    case 'password':
      return (
        hasInvalidPassword(value) &&
        buildErrorMessage('password with at least 8 characters')
      );
    case 'phone':
      return (
        !validatePhone(value) &&
        buildErrorMessage('valid 10-digit mobile number')
      );
    case 'zip':
      return (
        !isPostalCode(value) && buildErrorMessage(`valid ${zipCodeText} code`)
      );
    case 'state':
      return (
        hasInvalidState(value) && buildErrorMessage('valid state/province')
      );
    default:
      return null;
  }
};

export const getErrorMessage = (fieldName, value, zipCodeText, isOptional) =>
  isOptional && !value
    ? null
    : !value
    ? getEmptyErrorMessage(fieldName, zipCodeText)
    : getValidationErrorMessage(fieldName, value, zipCodeText);

export const getPostalCodeErrorMessage = (
  value,
  country,
  zipCodeText = 'ZIP / Postal'
) =>
  !value
    ? getEmptyErrorMessage('zip', zipCodeText)
    : !isPostalCode(value, country) &&
      buildErrorMessage(`valid ${zipCodeText} code`);

const getInvalidFields = (fields = {}) =>
  Object.entries(fields).reduce((invalidFields, [key, valueObject]) => {
    const errorMessage = getErrorMessage(key, valueObject);
    if (!!errorMessage) {
      invalidFields[key] = errorMessage;
    }
    return invalidFields;
  }, {});

export const hasValidFields = (fieldsToValidate = {}, saveFieldErrors) => {
  const fieldErrors = getInvalidFields(fieldsToValidate);
  saveFieldErrors && saveFieldErrors(fieldErrors);

  return objectIsEmpty(fieldErrors);
};

export const getTargetValue = (event) => event?.target?.value;

export const checkAndGetTargetValue = (event) => {
  const isTelType = event?.target?.type === 'tel';
  return isTelType ? removeNonTelCharacters(event) : getTargetValue(event);
};

export const removeNonTelCharacters = (event) => {
  const value = getTargetValue(event);
  return value.replace(/[^\d ().+-]/g, '');
};

export const getAllFormErrors = ({
  formFields,
  formValues = {},
  optionalFields,
  trackEvent,
  trackEventPath,
}) => {
  let formErrors = {};

  formFields.forEach(({ field }) => {
    const error =
      field === 'zip'
        ? getPostalCodeErrorMessage(formValues[field], formValues.country)
        : getErrorMessage(
            field,
            formValues[field],
            null,
            optionalFields?.includes(field)
          );
    if (!!error) {
      trackEvent && trackEvent(trackEventPath, { error, field });
      return (formErrors[field] = error);
    }
  });
  return formErrors;
};
